import { useState, useRef, useEffect } from "react";
import { useForm, usePage, router } from "@inertiajs/react";
import AppLayout from "@/layout/AppLayout";
import { Avatar } from "@/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LocationAutocomplete from "@/components/app/LocationAutocomplete";

export default function SettingsEdit() {
  const { user, errors = {}, routes } = usePage().props;
  const [usernameStatus, setUsernameStatus] = useState(null); // null | "checking" | "available" | "taken"

  // Stable initial display value for the city autocomplete (computed from user, not form)
  const [cityInitialValue] = useState(() => {
    const city  = user.city  ?? "";
    const state = user.state ?? "";
    return city && state ? `${city}, ${state}` : city;
  });

  function handleCitySelect(feature) {
    const city      = feature.text;
    const regionCtx = feature.context?.find((c) => c.id?.startsWith("region."));
    const state     = regionCtx?.short_code?.split("-")[1] ?? "";
    form.setData({ ...form.data, city, state });
  }

  const form = useForm({
    first_name:               user.first_name  ?? "",
    last_name:                user.last_name   ?? "",
    username:                 user.username    ?? "",
    city:                     user.city        ?? "",
    state:                    user.state       ?? "",
    bgg_username:             user.bgg_username ?? "",
    notify_event_reminders:   user.notify_event_reminders   ?? true,
    notify_post_event_prompt: user.notify_post_event_prompt ?? true,
  });

  useEffect(() => {
    const val = form.data.username.trim();
    if (!val || val === user.username) {
      setUsernameStatus(null);
      return;
    }
    setUsernameStatus("checking");
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${routes.username_check}?username=${encodeURIComponent(val)}`);
        const data = await res.json();
        setUsernameStatus(data.available ? "available" : "taken");
      } catch {
        setUsernameStatus(null);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [form.data.username]);

  function submit(e) {
    e.preventDefault();
    form.patch(routes.settings);
  }

  return (
    <AppLayout>
      <div className="container py-8 max-w-xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Profile settings</h1>

        <AvatarSection user={user} routes={routes} />

        <form onSubmit={submit} className="space-y-6">
          {/* Name */}
          <Section title="Name">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" id="first_name" error={errors.first_name}>
                <Input
                  id="first_name"
                  value={form.data.first_name}
                  onChange={(e) => form.setData("first_name", e.target.value)}
                  placeholder="First"
                />
              </Field>
              <Field label="Last name" id="last_name" error={errors.last_name}>
                <Input
                  id="last_name"
                  value={form.data.last_name}
                  onChange={(e) => form.setData("last_name", e.target.value)}
                  placeholder="Last"
                />
              </Field>
            </div>
          </Section>

          {/* Username */}
          <Section title="Username">
            <Field label="Username" id="username" error={errors.username}>
              <div className="flex items-center gap-0">
                <span className="flex items-center h-9 px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm select-none">
                  @
                </span>
                <Input
                  id="username"
                  value={form.data.username}
                  onChange={(e) => form.setData("username", e.target.value)}
                  placeholder="yourusername"
                  className="rounded-l-none rounded-r-none border-r-0"
                />
                <span className="flex items-center h-9 px-3 rounded-r-md border border-l-0 border-input bg-muted text-sm min-w-[90px]">
                  <UsernameStatus status={usernameStatus} />
                </span>
              </div>
            </Field>
          </Section>

          {/* Location */}
          <Section
            title="Location"
            description="Used to show nearby events and bias location searches."
          >
            <Field label="City" id="city" error={errors.city || errors.state}>
              <LocationAutocomplete
                value={cityInitialValue}
                types="place"
                placeholder="Portland, OR"
                onSelect={handleCitySelect}
                onChange={(val) => {
                  if (!val.trim()) form.setData({ ...form.data, city: "", state: "" });
                }}
              />
            </Field>
          </Section>

          {/* BoardGameGeek */}
          <Section title="BoardGameGeek" description="Link your BGG account to display your collection and play history on your profile.">
            <Field label="BGG username" id="bgg_username" error={errors.bgg_username}>
              <Input
                id="bgg_username"
                value={form.data.bgg_username}
                onChange={(e) => form.setData("bgg_username", e.target.value)}
                placeholder="your_bgg_name"
              />
            </Field>
          </Section>

          {/* Notifications */}
          <Section title="Notifications" description="Choose which emails you'd like to receive from GameKnight.">
            <div className="rounded-lg border border-border bg-card divide-y divide-border">
              <NotifToggle
                id="notify_event_reminders"
                label="Event reminders"
                description="24-hour reminder before events you're attending"
                checked={form.data.notify_event_reminders}
                onChange={(v) => form.setData("notify_event_reminders", v)}
              />
              <NotifToggle
                id="notify_post_event_prompt"
                label="Play log prompts"
                description="Prompt to log your plays after an event wraps up"
                checked={form.data.notify_post_event_prompt}
                onChange={(v) => form.setData("notify_post_event_prompt", v)}
              />
            </div>
          </Section>

          <div className="flex items-center justify-between pt-2">
            <Button type="submit" disabled={form.processing}>
              {form.processing ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

function AvatarSection({ user, routes }) {
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setPreviewFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }

  function handleSaveUpload() {
    if (!previewFile) return;
    setUploading(true);
    const data = new FormData();
    data.append("avatar", previewFile);
    router.patch(routes.settings_avatar, data, {
      forceFormData: true,
      onFinish: () => {
        setUploading(false);
        setPreviewFile(null);
        setPreviewUrl(null);
      },
    });
  }

  function handleCancelUpload() {
    setPreviewFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleImport() {
    router.post(routes.avatar_import);
  }

  function handleRemove() {
    router.delete(routes.avatar_destroy);
  }

  const displayUser = previewUrl ? { ...user, avatar_url: previewUrl } : user;

  return (
    <Section title="Avatar" className="mb-8">
      <div className="flex items-center gap-5 p-5 rounded-lg border border-border bg-card">
        <Avatar user={displayUser} size="lg" />
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Upload photo
            </Button>

            {user.bgg_username && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleImport}
              >
                Import from BGG
              </Button>
            )}

            {user.has_avatar && !previewFile && (
              <button
                type="button"
                onClick={handleRemove}
                className="text-xs text-destructive hover:underline px-1"
              >
                Remove photo
              </button>
            )}
          </div>

          {previewFile && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                onClick={handleSaveUpload}
                disabled={uploading}
              >
                {uploading ? "Saving…" : "Save photo"}
              </Button>
              <button
                type="button"
                onClick={handleCancelUpload}
                className="text-xs text-muted-foreground hover:underline"
              >
                Cancel
              </button>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            JPG, PNG or GIF. Max 5 MB.
          </p>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </Section>
  );
}

function Section({ title, description, children, className = "" }) {
  return (
    <div className={`space-y-3 ${className}`}>
      <div>
        <h2 className="text-sm font-semibold">{title}</h2>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, id, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function UsernameStatus({ status }) {
  if (!status) return null;
  if (status === "checking") return <span className="text-muted-foreground text-xs animate-pulse">Checking…</span>;
  if (status === "available") return <span className="text-green-600 text-xs font-medium">✓ Available</span>;
  return <span className="text-destructive text-xs font-medium">✗ Taken</span>;
}

function NotifToggle({ id, label, description, checked, onChange }) {
  return (
    <label htmlFor={id} className="flex items-center justify-between gap-4 px-4 py-3.5 cursor-pointer hover:bg-accent/40 transition-colors">
      <div>
        <p className="text-sm font-medium leading-none">{label}</p>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </div>
      {/* Simple toggle switch */}
      <div className="relative shrink-0">
        <input
          id={id}
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div className="w-10 h-6 rounded-full bg-muted peer-checked:bg-primary transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2" />
        <div className="absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
      </div>
    </label>
  );
}
